import { CheckoutStatusErrorResponse, CheckoutStatusRequest, CheckoutStatusResponse } from "@ssc-booking/api-contracts"
import { useActor } from "@xstate/react"
import { match } from "ts-pattern"
import * as F from "fp-ts/function"
import { ECommerceEvent, ECommerceState } from "../e-commerce-machine"
import {
    interceptErrors,
    retryDataLoadMachine,
    retryDataLoadTypes,
    RequestResponseChannel,
    constantDelay,
    concat,
    limitRetries,
} from "../../../../../_common/machines/retry-data-load-machine"

const types = retryDataLoadTypes(CheckoutStatusRequest, CheckoutStatusResponse, CheckoutStatusErrorResponse)
export type CheckoutStatusLoaderTypes = typeof types
export type CheckoutStatusLoaderState = CheckoutStatusLoaderTypes["State"]
export type CheckoutStatusLoaderSend = CheckoutStatusLoaderTypes["Send"]
export type CheckoutStatusLoaderActorRef = CheckoutStatusLoaderTypes["ActorRef"]

export const buildCheckoutStatusLoader = (websocketClient: RequestResponseChannel) =>
    retryDataLoadMachine({
        channel: websocketClient,
        Request: CheckoutStatusRequest,
        Success: CheckoutStatusResponse,
        Error: CheckoutStatusErrorResponse,
        action: "checkout-status",
        notifyParent,
        shouldRetry,
        retryPolicy: F.pipe(constantDelay(5000), concat(limitRetries(12))),
    })

export const useCheckoutStatusLoader = (
    state: ECommerceState,
): [CheckoutStatusLoaderState, CheckoutStatusLoaderSend] => {
    const CheckoutStatusLoader = state.children.checkoutStatusLoader as CheckoutStatusLoaderActorRef
    return useActor(CheckoutStatusLoader) as unknown as [CheckoutStatusLoaderState, CheckoutStatusLoaderSend]
}

const notifyParent = (
    ctx: CheckoutStatusLoaderTypes["Context"],
    ev: CheckoutStatusLoaderTypes["Events"],
): ECommerceEvent | undefined =>
    interceptErrors(
        (_, ev) =>
            match(ev)
                .with({ type: "LOAD_SUCCESS", response: { type: "started" } }, ({ response }) => ({
                    type: "CHECKOUT_STARTED" as const,
                    response,
                }))
                .with({ type: "LOAD_SUCCESS", response: { type: "completed" } }, ({ response }) => ({
                    type: "CHECKOUT_COMPLETED" as const,
                    response,
                }))
                .with({ type: "LOAD_SUCCESS", response: { type: "failed" } }, ({ response }) => ({
                    type: "CHECKOUT_FAILED" as const,
                    response,
                }))
                .otherwise(() => undefined),
        ctx,
        ev,
    )

const shouldRetry: CheckoutStatusLoaderTypes["ShouldRetry"] = (ev) =>
    match(ev)
        .with({ type: "LOAD_SUCCESS", response: { type: "empty" } }, F.constTrue)
        .with({ type: "LOAD_SUCCESS", response: { type: "validated" } }, F.constTrue)
        .with({ type: "LOAD_FATAL_ERROR", errorCode: "connectionClosed" }, F.constTrue)
        .with({ type: "LOAD_ERROR", response: { errorCode: "transient" } }, F.constTrue)
        .otherwise(F.constFalse)
